package com.bulut.mecitogullari.data.repository

import com.bulut.mecitogullari.data.model.DebtEntry
import com.bulut.mecitogullari.data.model.Supplier
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

interface DebtRepository {
    val suppliers: Flow<List<Supplier>>
    val entries: Flow<List<DebtEntry>>
    fun entriesFor(supplierId: String): Flow<List<DebtEntry>>
    suspend fun getEntry(entryId: String): DebtEntry?
    suspend fun upsertSupplier(supplier: Supplier)
    suspend fun upsertEntry(entry: DebtEntry)
    suspend fun deleteEntry(entryId: String)
    /** Removes the supplier together with its whole statement. */
    suspend fun deleteSupplier(supplierId: String)
}

class FirestoreDebtRepository : DebtRepository {

    override val suppliers: Flow<List<Supplier>> = userCollectionFlow(SUPPLIERS)

    override val entries: Flow<List<DebtEntry>> = userCollectionFlow(ENTRIES)

    override fun entriesFor(supplierId: String): Flow<List<DebtEntry>> =
        entries.map { list -> list.filter { it.supplierId == supplierId } }

    override suspend fun getEntry(entryId: String): DebtEntry? {
        val uid = currentUid() ?: return null
        val snapshot = userCollection(uid, ENTRIES).document(entryId).get()
        return if (snapshot.exists) runCatching { snapshot.data<DebtEntry>() }.getOrNull() else null
    }

    override suspend fun upsertSupplier(supplier: Supplier) {
        val uid = currentUid() ?: return
        userCollection(uid, SUPPLIERS).document(supplier.id).set(supplier)
    }

    override suspend fun upsertEntry(entry: DebtEntry) {
        val uid = currentUid() ?: return
        userCollection(uid, ENTRIES).document(entry.id).set(entry)
    }

    override suspend fun deleteEntry(entryId: String) {
        val uid = currentUid() ?: return
        userCollection(uid, ENTRIES).document(entryId).delete()
    }

    override suspend fun deleteSupplier(supplierId: String) {
        val uid = currentUid() ?: return
        val snapshot = userCollection(uid, ENTRIES).get()
        val toDelete = snapshot.documents.filter {
            runCatching { it.data<DebtEntry>().supplierId }.getOrNull() == supplierId
        }
        toDelete.chunked(400).forEach { chunk ->
            val batch = Firebase.firestore.batch()
            chunk.forEach { doc -> batch.delete(doc.reference) }
            batch.commit()
        }
        userCollection(uid, SUPPLIERS).document(supplierId).delete()
    }

    private companion object {
        const val SUPPLIERS = "suppliers"
        const val ENTRIES = "debtEntries"
    }
}
